import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import MLStringRichInputBoxViewModel from '@/ViewModels/Controls/MLStringRichInputBoxViewModel';
import MLStringRichInputBox from '@/Controls/MLStringRichInputBox';
import RichTextBoxViewModel from '@/ViewModels/Controls/RichTextBoxViewModel';
import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';

export default class MLStringRichInputBoxFactory extends ControlFactory {

    private jQueryControl: JQuery;
    private MLStringRichInputBoxViewModel = ComponentManager.getViewModel<typeof MLStringRichInputBoxViewModel>('MLStringRichInputBoxViewModel');
    private MLStringRichInputBox = ComponentManager.getControl<typeof MLStringRichInputBox>('MLStringRichInputBox');
    private RichTextBoxViewModel = ComponentManager.getViewModel<typeof RichTextBoxViewModel>('RichTextBoxViewModel');
    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    private technicalNameRootProperty: string;
    private technicalNameValueMLS: string;
    private technicalNameValueMLSForEach: string;
    private lastValue: string;
    private isValueModified = false;

    /**
     * @override
     */
    public viewModel: MLStringRichInputBoxViewModel;

    public constructor(params: any) {
        super(params);
        this.technicalNameRootProperty = '';
        this.technicalNameValueMLS = '';
        this.technicalNameValueMLSForEach = '';
    }

    /**
     * @override
     */
    control: MLStringRichInputBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = this.createViewModel();
        
        this.control = new this.MLStringRichInputBox(this.viewModel);
        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.technicalNameValueMLS], 'REVERT_MLSTRING_BUTTON_TOOLTIP');
        this.registerDefaultValidation([this.detailViewItem.name, this.technicalNameValueMLS, this.technicalNameValueMLSForEach]);
        this.registerCustomValidation([this.detailViewItem.name], this.viewModel.value, (value: string): boolean => {
            return Utilities.validateInputForInvalidCharacters(value);
        });

        return this.jQueryControl;
    }

    public getAvailableOperators(): Array<{ ergName: string, value: string }> {
        var result = [];

        result.push({ ergname: 'LIKE', value: 'LIKE' });

        return result;
    }

    public getValueForFilter(): Object {
        if (this.viewModel && !this.viewModel.isDisposed) {
            var ret = {};

            ret[this.technicalNameValueMLS] = this.viewModel.textbox.value();
            return ret;
        }

        return null;
    }

    public getFilterInfo(): FilterInfo {
        return {
            field: this.technicalNameValueMLS,
            value: this.viewModel?.textbox.value() || ''
        }
    }

    private createViewModel(): MLStringRichInputBoxViewModel {
        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            length = 1000;
        }

        this.validateTechName();

        this.context.genericObject.suppressNotificationFor(this.detailViewItem.name);
        
        return new this.MLStringRichInputBoxViewModel({
            factory: this,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            invalidationText: this.invalidationText,
            value: this.context.genericObject.getObservable(this.technicalNameValueMLS),
            separatorString: this.context.genericObject.getValue(this.technicalNameRootProperty + '.SeparatorString'),
            isEachLanguageEqual: this.context.genericObject.getValue(this.technicalNameValueMLSForEach),
            ergName: this.detailViewItem.ergName,
            maxLength: length,
            parentLocator: this.locator,
            locator: this.technicalNameRootProperty,
            onAfterInit: this.onAfterInit.bind(this),
            richTextBox: new this.RichTextBoxViewModel({
                factory: this,
                isEnabled: this.isEnabled,
                value: this.createValueObservable(),
                isFocusable: this.isFocusable,
                maxLength: length,
                parentLocator: this.locator,
                locator: this.detailViewItem.name,
                window: new this.WindowViewModel({
                    parentLocator: this.locator,
                    locator: this.technicalNameRootProperty + '.window',
                    width: 1000,
                    isModal: true,
                    isOpen: false,
                    allowClose: true,
                    headline: new this.LabelViewModel({
                        caption: ''
                    })
                })
            })
        });
    }

    private onAfterInit(): void { 
        this.control.onAfterInit();
    }

    private createValueObservable(): KnockoutObservable<any> {
        if (!this.detailViewItem.isDerivedOverwritable)
            return this.context.genericObject.getObservable(this.detailViewItem.name);

        // MLString Fallback Behandlung: Leerer Text darf bei DerivedOverwritable nicht dazu f�hren, dass ein Fallback Wert verwendet wird (SAGGRC-1498)
        return this.context.genericObject.getComputed(this.detailViewItem.name, {
            read: (obs: KnockoutObservable<string>) => {
                const value = obs();
                if (this.isValueModified)
                    return this.lastValue;

                // Sonderfall: Textbox wurde mit leerem String initialisiert und der Server liefert durch den Fallback einen Wert
                if (this.lastValue === '')
                    return this.lastValue;

                return value;
            },
            write: (obs: KnockoutObservable<string>, newValue: string) => {
                if (this.lastValue !== undefined)
                    this.isValueModified = true;

                this.lastValue = newValue;

                obs(newValue);
            }
        });
    }

    private validateTechName(): void {
        this.technicalNameRootProperty = this.detailViewItem.name;

        if (this.detailViewItem.name.substring(this.detailViewItem.name.length - 6) === '.Value') {
            this.technicalNameRootProperty = this.detailViewItem.name.substring(0, this.detailViewItem.name.length - 6);
        }

        this.technicalNameValueMLS = this.technicalNameRootProperty + '.ValueMLS';
        this.technicalNameValueMLSForEach = this.technicalNameRootProperty + '.ForEachLanguageEquals';
    }

}

ComponentManager.setFactory('MLStringRichInputBox', MLStringRichInputBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');